import { useState, useRef, useEffect } from "react";
import Modal from '../../../components/Modal';
import { useModal } from '../../../hooks/useModal';
import Button from '../../../components/Button';
import { useToastContext } from '../../../App';
import {
    prepareImageFileForUpload,
    isHeicLike,
    IMAGE_CONVERT_ERROR_MESSAGE,
} from '../../../utils/prepareImageFileForUpload';
import { formShell } from '../../../styles/formShell';
import { thumbnailShell } from '../../../styles/thumbnailShell';

function ThumbnailUploader({ thumbnailURL, thumbnailFile, onThumbnailChange, onRemoveThumbnail }) {
    const [previewURL, setPreviewURL] = useState(null);
    const [localFile, setLocalFile] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const fileInputRef = useRef(null);
    const previewURLRef = useRef(null);
    const { isOpen, openModal, closeModal } = useModal();
    const { showToast } = useToastContext();

    // previewURLの変更をrefに反映
    useEffect(() => {
        previewURLRef.current = previewURL;
    }, [previewURL]);

    // ローカルファイルが選択されている場合は、thumbnailURLの変更を無視
    // 削除意図（thumbnailFile === null）のときは thumbnailURL で復元しない
    useEffect(() => {
        if (localFile) {
            // ローカルファイルが選択されている場合は、previewURLを更新しない
            return;
        }
        if (thumbnailFile === null) {
            // ユーザーが削除した場合はプレビューを空にする
            setPreviewURL(null);
            return;
        }
        // ローカルファイルが選択されていない場合のみ、thumbnailURLからpreviewURLを設定
        setPreviewURL(thumbnailURL || null);
    }, [thumbnailURL, localFile, thumbnailFile]);

    // thumbnailFileが変更された場合、ローカルファイルとして設定
    useEffect(() => {
        if (thumbnailFile && thumbnailFile instanceof File) {
            // 以前のローカルURLをクリーンアップ
            setPreviewURL(prevURL => {
                if (prevURL && prevURL.startsWith('blob:')) {
                    URL.revokeObjectURL(prevURL);
                }
                return URL.createObjectURL(thumbnailFile);
            });
            setLocalFile(thumbnailFile);
        } else if (thumbnailFile === null) {
            // thumbnailFileがnullに設定された場合（削除）、プレビューをクリア（thumbnailURLで復元しない）
            setPreviewURL(prevURL => {
                if (prevURL && prevURL.startsWith('blob:')) {
                    URL.revokeObjectURL(prevURL);
                }
                return null;
            });
            setLocalFile(null);
        }
    }, [thumbnailFile, thumbnailURL]);

    // コンポーネントのアンマウント時にローカルURLをクリーンアップ
    useEffect(() => {
        return () => {
            if (previewURL && previewURL.startsWith('blob:')) {
                URL.revokeObjectURL(previewURL);
            }
        };
    }, [previewURL]);

    // サムネイルのバリデーション（バックエンドのvalidators.pyに合わせて統一）
    const validateThumbnailFile = (file) => {
        if (!file) {
            return { valid: false, error: 'ファイルが選択されていません。' };
        }

        // ファイルサイズチェック（10MB以下）
        const MAX_SIZE = 10 * 1024 * 1024; // 10MB
        if (file.size > MAX_SIZE) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            return { valid: false, error: `サムネイルのファイルサイズは10MB以下にしてください。選択されたファイル: ${fileSizeMB}MB` };
        }

        // 拡張子チェック
        const fileName = file.name.toLowerCase();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'webp', 'bmp'];
        const ext = fileName.split('.').pop();
        if (!allowedExtensions.includes(ext)) {
            return { valid: false, error: `サムネイルは${allowedExtensions.join('、')}形式でなければなりません。` };
        }

        return { valid: true };
    };

    const applyImageFile = async (file) => {
        const validation = validateThumbnailFile(file);
        if (!validation.valid) {
            showToast(validation.error, 'error');
            return;
        }

        const isImage = file.type.startsWith("image/") || isHeicLike(file);
        if (!isImage) {
            return;
        }

        setIsConverting(true);
        try {
            const ready = await prepareImageFileForUpload(file);
            const currentPreviewURL = previewURLRef.current;
            if (currentPreviewURL && currentPreviewURL.startsWith('blob:')) {
                URL.revokeObjectURL(currentPreviewURL);
            }
            const localURL = URL.createObjectURL(ready);
            setPreviewURL(localURL);
            setLocalFile(ready);
            onThumbnailChange(ready);
        } catch (err) {
            showToast(err?.message || IMAGE_CONVERT_ERROR_MESSAGE, 'error');
        } finally {
            setIsConverting(false);
        }
    };

    const handleInputChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = "";
        if (!file) return;
        await applyImageFile(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        if (isConverting) return;
        const file = e.dataTransfer.files[0];
        if (file) {
            await applyImageFile(file);
        }
    };

    const handleDeleteClick = () => {
        openModal();
    };

    const handleConfirmDelete = () => {
        closeModal();
        // ローカルURLをクリーンアップ
        const currentPreviewURL = previewURLRef.current;
        if (currentPreviewURL && currentPreviewURL.startsWith('blob:')) {
            URL.revokeObjectURL(currentPreviewURL);
        }
        setPreviewURL(null);
        setLocalFile(null);
        onRemoveThumbnail?.();
    };

    // ローカルファイルが選択されている場合も「更新」として扱う
    const isNewThumbnail = !thumbnailURL && !localFile;
    const dropZoneMessage = isNewThumbnail
        ? "サムネイルを追加"
        : "サムネイルを更新";

    const dropZoneLabel = isConverting ? "画像を変換しています…" : dropZoneMessage;

    return (
        <>
            <div className={thumbnailShell.uploader}>
                <label className={thumbnailShell.label}>サムネイル</label>

                <div className={thumbnailShell.horizontalLayout}>
                    {/* 左側：画像プレビュー */}
                    <div className={thumbnailShell.previewSection}>
                        {previewURL ? (
                            <div className={thumbnailShell.previewWrapper}>
                                <img
                                    src={previewURL}
                                    alt="サムネイル"
                                    className={thumbnailShell.previewImage}
                                />
                                <Button
                                    type="button"
                                    variant="danger"
                                    size="sm"
                                    className={thumbnailShell.removeButton}
                                    onClick={handleDeleteClick}
                                >
                                    削除
                                </Button>
                            </div>
                        ) : (
                            <div className={thumbnailShell.placeholder}>
                                <span>画像なし</span>
                            </div>
                        )}
                    </div>

                    {/* 右側：アップロードボックス */}
                    <div className={thumbnailShell.uploadSection}>
                        <div
                            className={`${isConverting ? formShell.dropzoneDisabled : formShell.dropzone} ${thumbnailShell.dropzone}`}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            onClick={() => {
                                if (!isConverting) fileInputRef.current.click();
                            }}
                            aria-busy={isConverting}
                        >
                            <p className={thumbnailShell.dropzoneMessage}>{dropZoneLabel}</p>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleInputChange}
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="サムネイルを削除"
                size="small"
                actions={[
                    { label: "キャンセル", variant: "secondary" },
                    { label: "削除", variant: "danger", onClick: handleConfirmDelete }
                ]}
            >
                <p>この操作は取り消せません。</p>
            </Modal>
        </>
    );
}

export default ThumbnailUploader;